import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

const DRAWER_VARIANTS_CACHE = 'data-quick-add-drawer-variants-cache';

/** @type {Promise<unknown> | null} */
let productFormModulePromise = null;

async function ensureProductFormModuleLoaded() {
  // When quick-add content is injected via DOM morph, any <script> tags inside it won't execute.
  // That can leave <product-form-component>/<add-to-cart-component> as unknown elements and make
  // the drawer ATC button appear "dead" (native submit is prevented by the drawer guard).
  if (customElements.get('product-form-component') && customElements.get('add-to-cart-component')) return;

  if (!productFormModulePromise) {
    productFormModulePromise = import('@theme/product-form').catch((err) => {
      productFormModulePromise = null;
      console.error('Quick Add: failed to load product-form module', err);
      throw err;
    });
  }

  await productFormModulePromise;
}

/**
 * Drawer-only: resolve hidden cart variant id from visible picker + full variant list
 * (cached from fetched PDP before .product-details is removed).
 * @param {HTMLElement} modalContent
 */
function getDrawerVariantsCache(modalContent) {
  const el = modalContent.querySelector(`script[${DRAWER_VARIANTS_CACHE}]`);
  if (!el?.textContent) return null;
  try {
    const parsed = JSON.parse(el.textContent.trim());
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {unknown} v
 * @param {string[]} selected
 */
function drawerVariantMatchesOptions(v, selected) {
  if (!v || typeof v !== 'object') return false;
  const o = /** @type {Record<string, unknown>} */ (v);
  for (let i = 0; i < selected.length; i++) {
    const key = `option${i + 1}`;
    const got = o[key];
    const gs = got == null ? '' : String(got).trim();
    const ws = (selected[i] || '').trim();
    if (gs !== ws) return false;
  }
  return true;
}

/**
 * @param {HTMLElement} variantPicker
 * @returns {string[]}
 */
function getSelectedOptionValuesFromDrawerPicker(variantPicker) {
  const fieldsets = variantPicker.querySelectorAll('fieldset');
  /** @type {string[]} */
  const values = [];
  for (const fs of fieldsets) {
    const hasRadios = fs.querySelector('input[type="radio"]');
    const hasSelect = fs.querySelector('select');
    if (!hasRadios && !hasSelect) continue;

    const radio = fs.querySelector('input[type="radio"]:checked');
    if (radio instanceof HTMLInputElement) {
      values.push(radio.value);
      continue;
    }
    const sel = fs.querySelector('select');
    if (sel instanceof HTMLSelectElement) {
      const opt = sel.options[sel.selectedIndex];
      values.push(opt ? opt.value : '');
    }
  }
  return values;
}

/**
 * @param {unknown[]} variants
 * @param {string[]} selected
 */
function findDrawerVariantId(variants, selected) {
  if (!variants.length || !selected.length) return null;
  for (const v of variants) {
    if (drawerVariantMatchesOptions(v, selected)) {
      const id = /** @type {Record<string, unknown>} */ (v).id;
      return id != null ? String(id) : null;
    }
  }
  return null;
}

function syncQuickAddDrawerVariantIdFromPicker() {
  const modalContent = document.getElementById('quick-add-drawer-content');
  if (!modalContent) return;

  const variants = getDrawerVariantsCache(modalContent);
  if (!variants?.length) return;

  const variantPicker = modalContent.querySelector('variant-picker');
  if (!variantPicker) return;

  const selected = getSelectedOptionValuesFromDrawerPicker(/** @type {HTMLElement} */ (variantPicker));
  if (!selected.length) return;

  const id = findDrawerVariantId(variants, selected);
  if (!id) return;

  const formComp = modalContent.querySelector('product-form-component');
  const input = /** @type {HTMLInputElement | null} */ (
    formComp?.querySelector('input[ref="variantId"]') || formComp?.querySelector('input[name="id"]')
  );
  if (!input) return;

  if (input.value !== id) {
    input.value = id;
    input.setAttribute('value', id);
  }
}

function queueSyncQuickAddDrawerVariantId() {
  queueMicrotask(() => syncQuickAddDrawerVariantIdFromPicker());
}

/** @type {boolean} */
let drawerVariantSyncListenersBound = false;

/** Theme / Swym sometimes add these to the drawer host and break layout or stacking — strip on the shell only. */
const QUICK_ADD_DRAWER_STRIP_HOST_CLASSES = [
  'page-width-narrow',
  'card-hover-effect-none',
  'swym-ready',
  'swym-buttons-loaded',
];

function stripQuickAddDrawerHostClasses() {
  /** @param {HTMLElement | null} el */
  const strip = (el) => {
    if (!el) return;
    for (const c of QUICK_ADD_DRAWER_STRIP_HOST_CLASSES) {
      el.classList.remove(c);
    }
  };

  strip(document.getElementById('quick-add-drawer'));
  strip(document.getElementById('quick-add-drawer-content'));
  const innerDialog = document.querySelector('#quick-add-drawer dialog.quick-add-drawer');
  strip(innerDialog instanceof HTMLElement ? innerDialog : null);
}

function removePdpSoldPiecesFromQuickAddTree(root) {
  if (!(root instanceof HTMLElement)) return;
  root.querySelectorAll('.pdp-sold-pieces, [data-pdp-sold-pieces]').forEach((node) => {
    node.remove();
  });
}

/**
 * PLP Quick Add only: block native POST to /cart/add and ensure a single AJAX path.
 * Runs on the form in capture phase before buybox-custom (registered on content-updated).
 */
/**
 * Product ATC forms in the drawer: `data-type="add-to-cart-form"` or Shopify product form shape
 * (variant id + add button) so we still intercept if an attribute is missing from fetched HTML.
 */
function isQuickAddDrawerProductAddForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  if (form.getAttribute('data-type') === 'add-to-cart-form') return true;
  const hasVariant = Boolean(form.querySelector('input[name="id"], input[ref="variantId"]'));
  const hasAdd = Boolean(form.querySelector('button[name="add"], [type="submit"][name="add"]'));
  return hasVariant && hasAdd;
}

function bindQuickAddDrawerProductFormSubmitExclusive(container) {
  if (!(container instanceof HTMLElement)) return;

  container.querySelectorAll('form').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (!isQuickAddDrawerProductAddForm(form)) return;
    if (form.dataset.quickAddDrawerSubmitExclusive === 'true') return;
    form.dataset.quickAddDrawerSubmitExclusive = 'true';

    form.addEventListener(
      'submit',
      /** @param {SubmitEvent} e */
      (e) => {
        const target = e.target;
        if (!(target instanceof HTMLFormElement) || target !== form) return;

        const drawerContent = document.getElementById('quick-add-drawer-content');
        if (!drawerContent?.contains(form)) return;

        const productFormEl = form.closest('product-form-component');
        const hasHandleSubmit =
          productFormEl != null && typeof /** @type {any} */ (productFormEl).handleSubmit === 'function';

        // If the drawer-level guard already handled it, don't do anything else.
        const anyEvent = /** @type {any} */ (e);
        if (anyEvent.__quickAddDrawerSubmitHandled) {
          e.stopImmediatePropagation();
          return;
        }

        e.preventDefault();

        if (hasHandleSubmit) {
          anyEvent.__quickAddDrawerSubmitHandled = true;
          e.stopImmediatePropagation();
          /** @type {any} */ (productFormEl).handleSubmit(e);
        }
      },
      true
    );
  });
}

function ensureQuickAddDrawerVariantSyncListeners() {
  if (drawerVariantSyncListenersBound) return;
  const drawer = document.getElementById('quick-add-drawer');
  if (!drawer) return;
  drawerVariantSyncListenersBound = true;

  drawer.addEventListener(ThemeEvents.variantUpdate, (e) => {
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;
    if (t.tagName.toLowerCase() !== 'variant-picker') return;
    if (!drawer.contains(t)) return;
    queueSyncQuickAddDrawerVariantId();
  });

  drawer.addEventListener(
    'change',
    (e) => {
      const el = e.target;
      if (!(el instanceof HTMLElement)) return;
      const content = document.getElementById('quick-add-drawer-content');
      if (!content?.contains(el)) return;
      if (!el.closest('variant-picker')) return;
      queueSyncQuickAddDrawerVariantId();
    },
    true
  );
}

/**
 * Opens the cart drawer (mini cart). Used after Quick Add ATC and shared with
 * QuickAddDialog so we do not rely on the unused `cart:open` event alone.
 */
function openMiniCartDrawer() {
  // The cart drawer section is fetched on-demand (layout/theme.liquid) rather than always
  // present in the DOM - window.ShopLC.openCartDrawer knows how to load it first if needed.
  // Fall back to the DOM-guessing approach below only if that API isn't available.
  if (window.ShopLC && typeof window.ShopLC.openCartDrawer === 'function') {
    window.ShopLC.openCartDrawer();
    return;
  }

  /** @type {any} */
  const cartDrawer = document.querySelector('cart-drawer-component');

  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
    return;
  }

  if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
    cartDrawer.showDialog();
    return;
  }

  if (cartDrawer) {
    customElements.whenDefined('cart-drawer-component').then(() => {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    });
    return;
  }

  /** @type {HTMLElement | null} */
  const trigger =
    document.querySelector('[data-testid="cart-drawer-trigger"]') ||
    document.querySelector('[data-cart-toggle]') ||
    document.querySelector('.header-actions__cart .button');
  if (trigger) trigger.click();
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Element | null} */
  #productCard = null;

  get productPageUrl() {
  const href = this.#getProductUrl();
  if (!href) return '';

  const url = new URL(href, window.location.origin);

  if (!url.searchParams.has('variant')) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }
  }

  return url.toString();
}

/**
 * Universal product URL resolver:
 * 1. data-product-url on quick-add-component
 * 2. nearest link marked for product url
 * 3. legacy product-card API
 * @returns {string}
 */
  #getProductUrl() {
    if (this.dataset.productUrl) return this.dataset.productUrl;

    const linkedEl =
      this.closest('[data-product-card-root]')?.querySelector('[data-product-url-link]') ||
      this.querySelector('[data-product-url-link]') ||
      this.closest('.recommendation-card')?.querySelector('.recommendation-card__image') ||
      this.closest('product-card')?.querySelector('a[href]');

    if (linkedEl instanceof HTMLAnchorElement && linkedEl.href) {
      return linkedEl.href;
    }

    const productCard = this.closest('product-card');
    if (productCard?.getProductCardLink) {
      return productCard.getProductCardLink()?.href || '';
    }

    return '';
  }

  /**
 * Universal selected variant resolver:
 * 1. data-selected-variant-id on quick-add-component
 * 2. hidden input[name="id"] in local form
 * 3. checked swatch/radio with data-variant-id
 * 4. legacy product-card API
 * @returns {string | null}
 */
  #getSelectedVariantId() {
    if (this.dataset.selectedVariantId) {
      return this.dataset.selectedVariantId;
    }

    const localVariantInput = this.querySelector('input[name="id"]');
    if (localVariantInput instanceof HTMLInputElement && localVariantInput.value) {
      return localVariantInput.value;
    }

    const checkedVariantInput = this.querySelector('input[type="radio"][data-variant-id]:checked');
    if (checkedVariantInput instanceof HTMLInputElement && checkedVariantInput.dataset.variantId) {
      return checkedVariantInput.dataset.variantId;
    }

    const root = this.closest('[data-product-card-root]');
    const rootVariantInput = root?.querySelector('input[name="id"]');
    if (rootVariantInput instanceof HTMLInputElement && rootVariantInput.value) {
      return rootVariantInput.value;
    }

    const productCard = this.closest('product-card');
    if (productCard?.getSelectedVariantId) {
      return productCard.getSelectedVariantId();
    }

    return null;
  }

  /**
   * Reads the variant from the clicked product-card trigger before opening the drawer.
   * @param {Event} event
   * @returns {string | null}
   */
  #getClickedVariantId(event) {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-action="open-quick-add-modal"], [data-variant-id]');
    const triggerVariantId = trigger instanceof HTMLElement ? trigger.getAttribute('data-variant-id') : '';
    if (triggerVariantId) return triggerVariantId;

    const card = target?.closest('.shoplc-product-card');
    const selectedSwatch =
      card?.querySelector('[data-card-swatch].shoplc-product-card__variant-swatch--selected') ||
      card?.querySelector('[data-card-swatch] input[type="radio"]:checked')?.closest('[data-card-swatch]');
    const swatchVariantId =
      selectedSwatch instanceof HTMLElement ? selectedSwatch.getAttribute('data-variant-id') : '';

    return swatchVariantId || null;
  }

  /**
   * Keeps the quick-add component, clicked button, and drawer pointed at one variant.
   * @param {string | null} variantId
   */
  #forceVariantForQuickAdd(variantId) {
    const drawer = document.getElementById('quick-add-drawer');

    if (!variantId) {
      drawer?.removeAttribute('data-forced-variant-id');
      return;
    }

    this.dataset.selectedVariantId = String(variantId);

    drawer?.setAttribute('data-forced-variant-id', String(variantId));

    const productUrl = this.#getProductUrl();
    if (productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', String(variantId));
      this.dataset.productUrl = url.toString();
    }

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(variantId);
      variantInput.setAttribute('value', String(variantId));
    }
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

     this.#productCard =
        this.closest('product-card') ||
        this.closest('.recommendation-card');

      if (this.#productCard) {
        this.#productCard.addEventListener('click', this.#handleCardClick);
      }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    if (this.#productCard) {
      this.#productCard.removeEventListener('click', this.#handleCardClick);
      this.#productCard = null;
    }
    this.#abortController?.abort();
  }

  /**
   * Delegated click: when "JETZT HINZUFÜGEN" (or any [data-action="open-quick-add-modal"]) is clicked,
   * open the quick-add modal instead of submitting a form.
   * @param {MouseEvent} event
   */
  #handleCardClick = (event) => {
    const target = /** @type {Element} */ (event.target).closest('[data-action="open-quick-add-modal"]');
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    this.handleClick(event);
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    this.#forceVariantForQuickAdd(this.#getClickedVariantId(event) || this.#getSelectedVariantId());

    if (this.dataset.directAtc === 'true') {
      this.#directAddToCart();
      return;
    }

    // Start loading the module early, but don't block opening the drawer UI on it.
    const ensureProductFormPromise = ensureProductFormModuleLoaded();
    // Avoid unhandled rejections in case the drawer cannot load PDP HTML for some reason.
    ensureProductFormPromise.catch(() => {});

    const drawerContent = document.getElementById('quick-add-drawer-content');
    if (drawerContent) drawerContent.replaceChildren();

    this.#openQuickAddModal();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await ensureProductFormPromise;
      await this.updateQuickAddModal(freshContent);
    }
  };

  /**
   * Adds the product directly to cart via AJAX and opens the mini cart drawer.
   * Used for products without variant options (has_only_default_variant).
   */
  async #directAddToCart() {
    const form = this.querySelector('form');
    if (!form) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[name="add"]'));

    if (variantInput?.disabled || button?.disabled) return;

    const variantId = variantInput?.value || variantInput?.getAttribute('value');
    if (!variantId) return;

    const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    const quantity = Number(quantityInput?.value) || 1;

    /** @type {HTMLElement | null} */
    const productFormComponent = this.querySelector('product-form-component');
    const bmsmEnabled = productFormComponent?.dataset?.bmsmEnabled === 'true';

    /** @type {Record<string, string> | null} */
    let properties = null;

    if (bmsmEnabled) {
      const groupId =
        productFormComponent?.dataset?.bmsmGroupId ||
        (productFormComponent?.dataset?.productId ? `bmsm-${productFormComponent.dataset.productId}` : `bmsm-${Date.now()}`);

      properties = {
        _bmsm_group_id: groupId,
        _bmsm_group_name: 'Buy More Save More',
        _bmsm_buy2: String(productFormComponent?.dataset?.bmsmBuy2Discount ?? ''),
        _bmsm_buy3: String(productFormComponent?.dataset?.bmsmBuy3Discount ?? ''),
        _bmsm_buy4: String(productFormComponent?.dataset?.bmsmBuy4Discount ?? ''),
        _bmsm_buy5: String(productFormComponent?.dataset?.bmsmBuy5Discount ?? ''),
      };
    }

    if (button) {
      button.setAttribute('disabled', '');
      button.classList.add('is-added');
    }

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
         },
         credentials: 'same-origin',
        body: JSON.stringify({ id: Number(variantId), quantity, ...(properties ? { properties } : {}) }),
      });

      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        console.error('Direct ATC error:', addData.description || addData.message);
        return;
      }

      const cartResponse = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      document.querySelectorAll(
        '[data-cart-count], [data-cart-icon-count], .cart-count, .header__cart-count, .header-actions__cart-count'
      ).forEach((el) => {
        el.textContent = String(cart.item_count || 0);
        if (cart.item_count > 0) {
          el.classList.remove('hidden', 'is-hidden');
          el.removeAttribute('hidden');
        }
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { addedItem: addData, cart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      openMiniCartDrawer();
    } catch (err) {
      console.error('Direct ATC fetch error:', err);
    } finally {
      if (button) {
        button.removeAttribute('disabled');
        setTimeout(() => button.classList.remove('is-added'), 2000);
      }
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-drawer');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-drawer');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-drawer-content');

    if (!productGrid || !modalContent) return;

    /** Full variant list lives under .product-details and is removed with it — preserve for drawer-only id sync. */
    let productVariantsCacheText = '';
    const variantsScriptEl = productGrid.querySelector('script[data-product-variants-json]');
    if (variantsScriptEl?.textContent) {
      productVariantsCacheText = variantsScriptEl.textContent.trim();
    }

    {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        removePdpSoldPiecesFromQuickAddTree(productFormComponent);
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    removePdpSoldPiecesFromQuickAddTree(modalContent);

    bindQuickAddDrawerProductFormSubmitExclusive(modalContent);

    if (productVariantsCacheText) {
      modalContent.querySelector(`script[${DRAWER_VARIANTS_CACHE}]`)?.remove();
      const cache = document.createElement('script');
      cache.type = 'application/json';
      cache.setAttribute(DRAWER_VARIANTS_CACHE, '');
      cache.textContent = productVariantsCacheText;
      modalContent.insertBefore(cache, modalContent.firstChild);
    }

    modalContent.querySelectorAll('variant-picker').forEach((picker) => {
      if (picker instanceof HTMLElement) {
        delete picker.dataset.availabilityInitialized;
      }
    });

    document.dispatchEvent(
      new CustomEvent('quick-add-drawer:content-updated', {
        bubbles: true,
        detail: { container: modalContent },
      })
    );

    ensureQuickAddDrawerVariantSyncListeners();

    this.#syncVariantSelection(modalContent);
    queueSyncQuickAddDrawerVariantId();

    queueMicrotask(() => {
      this.#injectWishlistIntoActions(modalContent);
    });

    stripQuickAddDrawerHostClasses();
    queueMicrotask(() => {
      stripQuickAddDrawerHostClasses();
    });
  }

  /**
   * Clones the wishlist button from the drawer footer into .pdp-buybox__actions so it appears left of Add to cart.
   * @param {Element} modalContent - The #quick-add-modal-content element
   */
  #injectWishlistIntoActions(modalContent) {
    const sourceToggle = document.querySelector('[data-quick-add-wishlist-source]');
    const template = document.getElementById('wishlist-icon-template');
    const actions = modalContent.querySelector('.pdp-buybox__actions');

    if (!sourceToggle || !template || !actions) return;

    actions
      .querySelectorAll('wishlist-product-icon[data-wishlist-context="quick-add-drawer"], .quick-add-drawer__wishlist')
      .forEach((el) => el.remove());

    const icon = template.content.cloneNode(true).querySelector('wishlist-product-icon');
    if (!icon) return;

    const config = document.querySelector('[data-wishlist-injector]');

    const formComp = modalContent.querySelector('product-form-component');
    const productIdInput = modalContent.querySelector('input[name="product-id"]');
    const variantInput =
      modalContent.querySelector('product-form-component input[ref="variantId"]') ||
      modalContent.querySelector('product-form-component input[name="id"]') ||
      modalContent.querySelector('input[name="id"]');

    const variantId =
      variantInput instanceof HTMLInputElement && variantInput.value
        ? variantInput.value
        : this.#getSelectedVariantId();

    let productId = '';

    if (formComp instanceof HTMLElement && formComp.dataset.productId) {
      productId = formComp.dataset.productId;
    } else if (productIdInput instanceof HTMLInputElement && productIdInput.value) {
      productId = productIdInput.value;
    } else {
      const productIdSource = modalContent.querySelector('[data-product-id]');
      productId = productIdSource?.getAttribute('data-product-id') || '';
    }

    let productHandle = '';
    try {
      const url = new URL(this.productPageUrl, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      productHandle = match ? decodeURIComponent(match[1]) : '';
    } catch (e) {
      productHandle = '';
    }

    let sku = '';
    const variants = getDrawerVariantsCache(modalContent) || [];
    const currentVariant = variants.find((variant) => {
      return variant && String(variant.id) === String(variantId);
    });

    if (currentVariant && typeof currentVariant === 'object' && currentVariant.sku) {
      sku = String(currentVariant.sku);
    }

    if (productId) icon.setAttribute('data-product-id', productId);
    if (productHandle) icon.setAttribute('data-product-handle', productHandle);
    if (variantId) icon.setAttribute('data-variant-id', String(variantId));
    if (sku) icon.setAttribute('data-sku', sku);

    icon.setAttribute('data-wishlist-context', 'quick-add-drawer');
    icon.setAttribute('data-source', config?.getAttribute('data-source') || window.location.hostname);
    icon.setAttribute('data-login-url', config?.getAttribute('data-login-url') || '/account/login');

    const customerEmail = config?.getAttribute('data-customer-email');
    if (customerEmail) icon.setAttribute('data-customer-email', customerEmail);

    const filledColor = config?.getAttribute('data-icon-color-filled') || '#C82222';
    const emptyColor = config?.getAttribute('data-icon-color-empty') || '#191919';
    const iconSize = config?.getAttribute('data-icon-size') || '24';

    icon.setAttribute('data-icon-color-filled', filledColor);
    icon.setAttribute('data-icon-color-empty', emptyColor);
    icon.setAttribute('data-icon-size', iconSize);

    icon.style.setProperty('--wishlist-icon-color-filled', filledColor);
    icon.style.setProperty('--wishlist-icon-color-empty', emptyColor);

    icon.classList.add('quick-add-drawer__wishlist');

    icon.querySelectorAll('svg').forEach((svg) => {
      svg.setAttribute('width', iconSize);
      svg.setAttribute('height', iconSize);
    });

    actions.prepend(icon);

    const syncWishlistVariant = () => {
      queueMicrotask(() => {
        syncQuickAddDrawerVariantIdFromPicker();

        const liveVariantInput =
          modalContent.querySelector('product-form-component input[ref="variantId"]') ||
          modalContent.querySelector('product-form-component input[name="id"]') ||
          modalContent.querySelector('input[name="id"]');

        const newVariantId =
          liveVariantInput instanceof HTMLInputElement && liveVariantInput.value
            ? liveVariantInput.value
            : null;

        if (!newVariantId) return;

        icon.setAttribute('data-variant-id', String(newVariantId));

        const freshVariants = getDrawerVariantsCache(modalContent) || [];
        const freshVariant = freshVariants.find((variant) => {
          return variant && String(variant.id) === String(newVariantId);
        });

        const newSku =
          freshVariant && typeof freshVariant === 'object' && freshVariant.sku
            ? String(freshVariant.sku)
            : '';

        if (newSku) {
          icon.setAttribute('data-sku', newSku);
        } else {
          icon.removeAttribute('data-sku');
        }

        if (typeof icon.checkWishlistStatus === 'function') {
          icon.checkWishlistStatus(true);
        }
      });
    };

    modalContent.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (!target.closest('variant-picker')) return;
        syncWishlistVariant();
      },
      true
    );

    const drawer = document.getElementById('quick-add-drawer');
    drawer?.addEventListener('variant:update', syncWishlistVariant);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const want = String(selectedVariantId);
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        String(input.dataset.variantId) === want &&
        !input.checked
      ) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #drawerSubmitGuardController = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.id === 'quick-add-drawer') {
      this.#drawerSubmitGuardController = new AbortController();
      const { signal } = this.#drawerSubmitGuardController;
      this.addEventListener(
        'submit',
        /** @param {SubmitEvent} e */
        (e) => {
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (!form.closest('#quick-add-drawer-content')) return;
          if (!isQuickAddDrawerProductAddForm(form)) return;
          // Always block native navigation POST from the drawer.
          e.preventDefault();

          // Fallback: ensure AJAX add-to-cart runs even if the per-form binder didn't attach
          // (e.g. content injected/morphed in a way that skipped bindQuickAddDrawerProductFormSubmitExclusive).
          const anyEvent = /** @type {any} */ (e);
          if (anyEvent.__quickAddDrawerSubmitHandled) return;

          const productFormEl = form.closest('product-form-component');
          const hasHandleSubmit =
            productFormEl != null && typeof /** @type {any} */ (productFormEl).handleSubmit === 'function';

          if (hasHandleSubmit) {
            anyEvent.__quickAddDrawerSubmitHandled = true;
            e.stopImmediatePropagation();
            /** @type {any} */ (productFormEl).handleSubmit(e);
          }
        },
        { capture: true, signal }
      );
    }

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#drawerSubmitGuardController?.abort();
    this.#drawerSubmitGuardController = null;
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    if (this.id === 'quick-add-drawer') {
      this.closeDialog();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          openMiniCartDrawer();
        });
      });
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
